import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, inr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Field, Select, Btn, MpinField, ReceiptDialog } from "@/components/shared";

export function useMeta() {
  const [meta, setMeta] = useState(null);
  useEffect(() => { api.get("/meta").then(({ data }) => setMeta(data)); }, []);
  return meta;
}

export function useSubmit(after) {
  const { refreshBalance } = useAuth();
  const [loading, setLoading] = useState(false);
  const [receipt, setReceipt] = useState(null);
  const run = async (url, body, okMsg) => {
    setLoading(true);
    try {
      const { data } = await api.post(url, body);
      if (data.status === "FAILED") toast.error(data.details?.reason || "Transaction failed");
      else toast.success(okMsg || "Transaction successful");
      setReceipt(data);
      refreshBalance();
      after && after(data);
      return data;
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  };
  return { loading, receipt, setReceipt, run };
}

function PlansDialog({ open, onClose, kind, operator, onPick }) {
  const [plans, setPlans] = useState([]);
  const [tab, setTab] = useState("");
  useEffect(() => {
    if (open) api.get("/plans", { params: { kind, operator } }).then(({ data }) => { setPlans(data.plans); setTab(data.plans[0]?.category); });
  }, [open, kind, operator]);
  const cats = [...new Set(plans.map((p) => p.category))];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="plans-dialog">
        <DialogHeader><DialogTitle className="font-display">{operator || "Operator"} plans</DialogTitle></DialogHeader>
        <div className="flex flex-wrap gap-2">
          {cats.map((c) => (
            <button key={c} onClick={() => setTab(c)} className={`rounded-full px-3 py-1 text-xs font-semibold ${tab === c ? "bg-blue-700 text-white" : "bg-slate-100"}`} data-testid={`plan-tab-${c}`}>{c}</button>
          ))}
        </div>
        <div className="max-h-[50vh] space-y-2 overflow-y-auto">
          {plans.filter((p) => p.category === tab).map((p) => (
            <button key={p.amount + p.desc} onClick={() => { onPick(p.amount); onClose(); }} className="flex w-full items-center justify-between rounded-lg border border-slate-200 p-3 text-left hover:border-blue-400" data-testid={`plan-option-${p.amount}`}>
              <div><div className="text-sm font-medium">{p.desc}</div><div className="text-xs text-slate-500">Validity: {p.validity}</div></div>
              <div className="font-mono text-lg font-bold text-blue-700">₹{p.amount}</div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function Recharge({ kind }) {
  const meta = useMeta();
  const blank = { number: "", operator: "", circle: "", amount: "", mpin: "" };
  const [f, setF] = useState(blank);
  const [plansOpen, setPlansOpen] = useState(false);
  const { loading, receipt, setReceipt, run } = useSubmit(() => setF(blank));
  useEffect(() => setF(blank), [kind]); // eslint-disable-line

  const lookup = async (num) => {
    if (kind !== "mobile" || num.length !== 10) return;
    try {
      const { data } = await api.get(`/operator-lookup/${num}`);
      setF((p) => ({ ...p, operator: data.operator, circle: data.circle }));
    } catch {}
  };

  const submit = (e) => {
    e.preventDefault();
    run("/recharge", { ...f, kind, amount: Number(f.amount) }, "Recharge successful");
  };

  const isMobile = kind === "mobile";
  return (
    <div>
      <PageHeader title={isMobile ? "Mobile Recharge" : "DTH Recharge"} subtitle={isMobile ? "Prepaid recharge with auto operator & circle detection." : "Recharge any DTH subscriber ID instantly."} />
      <div className="grid gap-6 lg:grid-cols-[1fr_380px]">
        <Card>
          <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="recharge-form">
            <Field label={isMobile ? "Mobile Number" : "Subscriber / VC Number"}>
              <input className="np-input font-mono" maxLength={isMobile ? 10 : 14} value={f.number}
                onChange={(e) => { const v = e.target.value.replace(/\D/g, ""); setF({ ...f, number: v }); lookup(v); }} required data-testid="recharge-number-input" />
            </Field>
            <Field label="Operator">
              <Select options={meta?.operators[kind] || []} placeholder="Select Operator" value={f.operator} onChange={(e) => setF({ ...f, operator: e.target.value })} required data-testid="recharge-operator-select" />
            </Field>
            {isMobile && (
              <Field label="Circle">
                <Select options={meta?.circles || []} placeholder="Select Circle" value={f.circle} onChange={(e) => setF({ ...f, circle: e.target.value })} required data-testid="recharge-circle-select" />
              </Field>
            )}
            <Field label="Amount">
              <div className="flex gap-2">
                <input type="number" className="np-input font-mono" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} required data-testid="recharge-amount-input" />
                <Btn type="button" variant="outline" onClick={() => (f.operator ? setPlansOpen(true) : toast.error("Please select operator"))} data-testid="browse-plans-button">Plans</Btn>
              </div>
            </Field>
            <MpinField value={f.mpin} onChange={(v) => setF({ ...f, mpin: v })} />
            <div className="flex items-end sm:col-span-2">
              <Btn type="submit" loading={loading} className="w-full sm:w-auto" data-testid="recharge-submit-button">Recharge {f.amount ? inr(f.amount) : ""}</Btn>
            </div>
          </form>
        </Card>
        <Card title="Commission">
          <div className="space-y-2 text-sm">
            {(isMobile ? [["Jio", "1.0%"], ["Airtel", "1.5%"], ["Vi", "3.0%"], ["BSNL", "4.0%"]] : [["All DTH operators", "3.0%"]]).map(([a, b]) => (
              <div key={a} className="flex justify-between rounded bg-slate-50 px-3 py-2"><span>{a}</span><b className="text-emerald-700">{b}</b></div>
            ))}
            <p className="text-xs text-slate-400">2% TDS deducted on commission.</p>
          </div>
        </Card>
      </div>
      <PlansDialog open={plansOpen} onClose={() => setPlansOpen(false)} kind={kind} operator={f.operator} onPick={(a) => setF((p) => ({ ...p, amount: String(a) }))} />
      <ReceiptDialog txn={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}

export function CreditCard() {
  const meta = useMeta();
  const blank = { card_number: "", card_holder: "", bank: "", mobile: "", amount: "", mpin: "" };
  const [f, setF] = useState(blank);
  const { loading, receipt, setReceipt, run } = useSubmit(() => setF(blank));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <PageHeader title="Credit Card Bill Payment" subtitle="Pay any bank's credit card bill. 0.5% convenience charge." />
      <Card className="max-w-3xl">
        <form onSubmit={(e) => { e.preventDefault(); run("/credit-card", { ...f, amount: Number(f.amount) }, "Card bill paid"); }} className="grid gap-4 sm:grid-cols-2" data-testid="cc-form">
          <Field label="Card Number"><input className="np-input font-mono" maxLength={19} value={f.card_number} onChange={set("card_number")} required data-testid="cc-number-input" /></Field>
          <Field label="Card Holder Name"><input className="np-input" value={f.card_holder} onChange={set("card_holder")} required data-testid="cc-holder-input" /></Field>
          <Field label="Issuing Bank"><Select options={meta?.banks || []} placeholder="Select Bank" value={f.bank} onChange={set("bank")} required data-testid="cc-bank-select" /></Field>
          <Field label="Customer Mobile"><input className="np-input" maxLength={10} value={f.mobile} onChange={set("mobile")} required data-testid="cc-mobile-input" /></Field>
          <Field label="Amount"><input type="number" className="np-input font-mono" value={f.amount} onChange={set("amount")} required data-testid="cc-amount-input" /></Field>
          <MpinField value={f.mpin} onChange={(v) => setF({ ...f, mpin: v })} />
          <div className="sm:col-span-2 text-xs text-slate-500">Total debit: <b>{inr(Number(f.amount || 0) * 1.005)}</b></div>
          <Btn type="submit" loading={loading} className="sm:w-48" data-testid="cc-submit-button">Pay Bill</Btn>
        </form>
      </Card>
      <ReceiptDialog txn={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}

export function CashDeposit() {
  const meta = useMeta();
  const blank = { bank: "", account_no: "", customer_name: "", mobile: "", amount: "", mpin: "" };
  const [f, setF] = useState(blank);
  const { loading, receipt, setReceipt, run } = useSubmit(() => setF(blank));
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div>
      <PageHeader title="Cash Deposit" subtitle="Deposit customer cash into any bank account. Amount debited from main wallet; Rs 4 commission." />
      <Card className="max-w-3xl">
        <form onSubmit={(e) => { e.preventDefault(); run("/cash-deposit", { ...f, amount: Number(f.amount) }, "Cash deposited"); }} className="grid gap-4 sm:grid-cols-2" data-testid="cash-deposit-form">
          <Field label="Bank"><Select options={meta?.banks || []} placeholder="Select Bank" value={f.bank} onChange={set("bank")} required data-testid="cd-bank-select" /></Field>
          <Field label="Account Number"><input className="np-input font-mono" value={f.account_no} onChange={set("account_no")} required data-testid="cd-account-input" /></Field>
          <Field label="Customer Name"><input className="np-input" value={f.customer_name} onChange={set("customer_name")} required data-testid="cd-name-input" /></Field>
          <Field label="Customer Mobile"><input className="np-input" maxLength={10} value={f.mobile} onChange={set("mobile")} required data-testid="cd-mobile-input" /></Field>
          <Field label="Amount (100 - 10000)"><input type="number" className="np-input font-mono" value={f.amount} onChange={set("amount")} required data-testid="cd-amount-input" /></Field>
          <MpinField value={f.mpin} onChange={(v) => setF({ ...f, mpin: v })} />
          <Btn type="submit" loading={loading} className="sm:w-48" data-testid="cd-submit-button">Deposit</Btn>
        </form>
      </Card>
      <ReceiptDialog txn={receipt} onClose={() => setReceipt(null)} />
    </div>
  );
}

export function PanVerify() {
  const [pan, setPan] = useState("");
  const { loading, receipt, setReceipt, run } = useSubmit();
  return (
    <div>
      <PageHeader title="PAN Verification" subtitle="Verify any PAN instantly. Rs 2 per verification." />
      <Card className="max-w-xl">
        <form onSubmit={(e) => { e.preventDefault(); run("/pan-verify", { pan }, "PAN verified"); }} className="flex flex-wrap items-end gap-3" data-testid="pan-form">
          <Field label="PAN Number" className="flex-1"><input className="np-input font-mono uppercase" maxLength={10} value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} placeholder="ABCDE1234F" required data-testid="pan-input" /></Field>
          <Btn type="submit" loading={loading} data-testid="pan-verify-button">Verify</Btn>
        </form>
        {receipt && (
          <div className="mt-5 rounded-lg bg-emerald-50 p-4 text-sm" data-testid="pan-result">
            <div className="font-semibold text-emerald-800">{receipt.details.pan_status}</div>
            <div>Name: <b data-testid="pan-result-name">{receipt.details.name}</b></div>
            <div>Aadhaar linked: {receipt.details.aadhaar_linked ? "Yes" : "No"}</div>
          </div>
        )}
      </Card>
    </div>
  );
}
